import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Form } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { DeleteUserConfirmModalLiterals } from 'components/molecules/DeleteUserConfirmModal';
import { ROUTE_PATH_USERS } from 'components/pages/App/routes/users/config';
import { SendNotificationModel } from 'core/domain/notifications/model';
import { SendNotification } from 'core/domain/notifications/repositories/sendNotification';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { User, UserRoleType, UserStatus } from 'models/users.model';
import { deleteUser, getProfileUser, getUser } from 'services/api/users';
import { EditUserModalLiterals } from 'components/molecules/EditUserModal';
import { EditUserFormValues, EditUserFormValuesNames } from 'components/molecules/EditUserModal/resources/config';
import UserProfileContext, { UserProfileContextState } from 'context/UserProfileContext';
import { useMessage } from 'hooks/useMessage';
import { UpdateUserListModel, UpdateUserListUserModel } from 'core/domain/users/models';
import { UpdateUserList } from 'core/domain/users/repositories/updateUserList';
import { GetClientsList } from 'core/domain/client/repositories/getClientsList';
import { ClientModel } from 'core/domain/client/models/clientEntityModel';
import { GetClientById } from 'core/domain/client/repositories/getClientById';
import { getClientPathById } from 'components/pages/App/routes/settings/config';

const STATUS_USER: UserStatus[] = ['pending', 'confirmation', 'withdrawn'];

export const useUser = () => {
  const { t } = useTranslation();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const { userId, tab } = useParams<{ userId: string; tab: string }>();
  const { setMessageError, setMessageSuccess } = useMessage();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showingPop, setShowingPop] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [user, setUser] = useState<User>();
  const [currentUser, setCurrentUser] = useState('');
  const [activeTab, setActiveTab] = useState(tab);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [isSendNotificationModalVisible, setIsSendNotificationModalVisible] = useState(false);
  const [isEditUserModalVisible, setIsEditUserModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [editingUser, setEditingUser] = useState<boolean>(false);
  const [clientList, setClientList] = useState<{ value: string; label: string }[]>([]);
  const { userProfile } = useContext<UserProfileContextState>(UserProfileContext);
  const [currentClient, setCurrentClient] = useState<string>('');

  const isCurrentUserSuperAdmin = userProfile?.roles.includes(UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN) || false;

  const userPageLiterals = {
    userLabel: t('user'),
    userStatus: user && t(STATUS_USER[user.status]),
    userRoles: (user && user?.roles.map((role) => t(`ROLES.${role}`)).join(', ')) || t('not_reported'),
    userName: t('userName'),
    email: t('email'),
    notReported: t('not_reported'),
    roles: t('roles'),
    status: t('status'),
    assets: t('assets'),
    unknown: t('unknown'),
    createdAt: t('created_at'),
    confirmedAt: t('confirmed_at'),
    deleteUser: t('delete_user'),
    sendNotification: t('send_notification'),
    editUserButtonText: t('_USER_PAGE_EDIT_USER_BUTTON_TEXT'),
    clientLabel: t('_EDIT_USER_MODAL_CLIENT_LABEL'),
  };

  const sendNotificationFormLiterals = {
    formTitleLabel: t('_SEND_NOTIFICATION_FORM_TITLE_LABEL'),
    formTitlePlaceholder: t('_SEND_NOTIFICATION_FORM_TITLE_PLACEHOLDER'),
    formMessageLabel: t('_SEND_NOTIFICATION_FORM_MESSAGE_LABEL'),
    formMessagePlaceholder: t('_SEND_NOTIFICATION_FORM_MESSAGE_PLACEHOLDER'),
    formFieldRequiredMeesage: t('_FIELD_REQUIRED'),
  };
  const sendNotificationModalLiterals = {
    modalTitle: t('_SEND_NOTIFICATION_FORM_TITLE'),
    modalCancelButton: t('_SEND_NOTIFICATION_FORM_CANCEL'),
    modalSendButton: t('_SEND_NOTIFICATION_FORM_SEND'),
  };

  const deleteUserModalLiterals: DeleteUserConfirmModalLiterals = {
    title: t('_DELETE_USER_CONFIRM_MODAL_TITLE'),
    secondaryButtonText: t('to_cancel'),
    mainButtonText: t('delete'),
    text: t('delete_user_confirm'),
  };

  const editUserModalLiterals: EditUserModalLiterals = {
    title: t('_EDIT_USER_MODAL_TITLE'),
    mainButtonText: t('_EDIT_USER_MODAL_MAIN_BUTTON_TEXT'),
    secondaryButtonText: t('cancel'),
    requiredFieldText: t('required_field'),
    usernameLabel: t('_EDIT_USER_MODAL_USERNAME_LABEL'),
    usernamePlaceholder: t('_EDIT_USER_MODAL_USERNAME_PLACEHOLDER'),
    clientLabel: t('_EDIT_USER_MODAL_CLIENT_LABEL'),
    rolesLabel: t('_EDIT_USER_MODAL_ROLES_LABEL'),
    rolesPlaceholder: t('_EDIT_USER_MODAL_ROLES_PLACEHOLDER'),
  };

  const rolesLiteralsManager: Record<string, string> = {
    [UserRoleType.ROLE_USER]: t(`ROLES.${UserRoleType.ROLE_USER}`),
    [UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN]: t(`ROLES.${UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN}`),
    [UserRoleType.ROLE_ASSETS_MANAGER_PROPERTY_MANAGER]: t(`ROLES.${UserRoleType.ROLE_ASSETS_MANAGER_PROPERTY_MANAGER}`),
    [UserRoleType.ROLE_ASSETS_MANAGER_USER]: t(`ROLES.${UserRoleType.ROLE_ASSETS_MANAGER_USER}`),
    [UserRoleType.ROLE_ASSETS_MANAGER_ADMIN]: t(`ROLES.${UserRoleType.ROLE_ASSETS_MANAGER_ADMIN}`),
    [UserRoleType.ROLE_ADMIN]: t(`ROLES.${UserRoleType.ROLE_ADMIN}`),
    [UserRoleType.ROLE_ALFRED_EXPERT]: t(`ROLES.${UserRoleType.ROLE_ALFRED_EXPERT}`),
    [UserRoleType.ROLE_ALFRED_PRO]: t(`ROLES.${UserRoleType.ROLE_ALFRED_PRO}`),
  };

  const userRoleList = [
    {
      label: rolesLiteralsManager[UserRoleType.ROLE_USER],
      value: UserRoleType.ROLE_USER,
    },
    {
      label: rolesLiteralsManager[UserRoleType.ROLE_ASSETS_MANAGER_ADMIN],
      value: UserRoleType.ROLE_ASSETS_MANAGER_ADMIN,
    },
    {
      label: rolesLiteralsManager[UserRoleType.ROLE_ALFRED_EXPERT],
      value: UserRoleType.ROLE_ALFRED_EXPERT,
    },
    {
      label: rolesLiteralsManager[UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN],
      value: UserRoleType.ROLE_ASSETS_MANAGER_SUPER_ADMIN,
    },
  ];

  useEffect(() => {
    setActiveTab(tab);
  }, [tab]);

  useEffect(() => {
    !!userId && getUserData(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    getProfileUser()
      .then(({ id }) => setCurrentUser(id))
      .finally(() => setLoading(false));
  }, []);

  const getUserData = async (userId: string) => {
    try {
      setLoading(true);
      const userData = await getUser(userId);
      const clients = await GetClientsList();
      const parsedClients = !!clients?.length ? parseClientList(clients) : [];
      if (!!userData) {
        const currentClientData = await GetClientById(userData.data.client.client_id);
        !!currentClientData && setCurrentClient(currentClientData.name);
      }
      setClientList(parsedClients);
      !!userData && setUser(userData.data);
      !!userData && !!clients && setEditUserFormValues({ data: userData.data, clients });
    } finally {
      setLoading(false);
    }
  };
  const parseClientList = (clients: ClientModel[]): { value: string; label: string }[] => {
    return clients.map((client) => ({ value: client.id, label: client.name }));
  };

  const setEditUserFormValues = ({ data, clients }: { data: User; clients: ClientModel[] }) => {
    const parsedRoles: DefaultOptionType[] = data.roles.map((role) => {
      return { label: rolesLiteralsManager[role as string], value: role };
    });

    const selectedClient = clients.find((client) => client.id === data.client.client_id);
    const parsedClient = selectedClient ? { value: selectedClient.id, label: selectedClient.name } : { value: '', label: '' };

    form.setFieldsValue({
      [EditUserFormValuesNames.USERNAME]: data.username,
      [EditUserFormValuesNames.ROLES]: parsedRoles,
      [EditUserFormValuesNames.CLIENT]: parsedClient,
    });
  };

  const onGoBack = () => history.goBack();

  const onShowDeleteUserPop = (show: boolean) => setShowingPop(show);

  const handleDeleteUser = async (user: User) => {
    setDeletingUser(true);
    try {
      const response = await deleteUser(user.id);
      setMessageSuccess({ description: t('deleted_user') });
      history.push(ROUTE_PATH_USERS);
      return response;
    } catch (error) {
      setMessageError({ description: t('delete_error_message') });
    } finally {
      setDeletingUser(false);
    }
  };

  const onChangeTab = (activeTab: string) => history.push(activeTab);

  const onClickSendNotification = () => {
    setIsSendNotificationModalVisible(true);
  };

  const onCancelSendNotification = () => {
    setIsSendNotificationModalVisible(false);
  };

  const onSendNotification = async ({ title, content }: SendNotificationModel) => {
    setIsSendingNotification(true);
    try {
      const userEmail = user?.email || '';
      if (!userEmail) throw new Error();

      const notification = {
        title,
        content,
        userList: [userEmail],
      };
      await SendNotification({
        token,
        host,
        notification,
      });
      setMessageSuccess({ description: t('_SEND_NOTIFICATION_FORM_SEND_SUCCESS') });
      setIsSendNotificationModalVisible(false);
    } catch (error) {
      setMessageError({ description: t('_SEND_NOTIFICATION_FORM_SEND_ERROR') });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const onClickEditUser = () => {
    setIsEditUserModalVisible(true);
  };

  const onCloseEditUser = () => {
    setIsEditUserModalVisible(false);
  };

  const getRolesValues = (values: Record<string, string>[]): string[] => {
    return values.map(({ value }) => value);
  };

  const parseRolesValueToBeSent = (values: EditUserFormValues[EditUserFormValuesNames.ROLES] | string[]): string[] => {
    const areValuesStrings = !!values.length && typeof values[0] === 'string';
    return areValuesStrings ? (values as string[]) : getRolesValues(values as Record<string, string>[]);
  };

  const parseClientValueToBeSent = (value: EditUserFormValues[EditUserFormValuesNames.CLIENT] | string): string => {
    return typeof value === 'string' ? value : value.value;
  };

  const onEditUser = async (values: EditUserFormValues) => {
    setEditingUser(true);

    const hasValuesClientId = !!values[EditUserFormValuesNames.CLIENT];
    const client: Record<string, string> = form.getFieldValue(EditUserFormValuesNames.CLIENT);

    const defaultUser: UpdateUserListUserModel = {
      id: user?.id || '',
      username: values[EditUserFormValuesNames.USERNAME],
      lang: user?.lang || 'es_ES',
      clientId: hasValuesClientId ? parseClientValueToBeSent(values[EditUserFormValuesNames.CLIENT]) : client.value,
    };

    const parsedUser: UpdateUserListUserModel = isCurrentUserSuperAdmin
      ? { ...defaultUser, roles: parseRolesValueToBeSent(values[EditUserFormValuesNames.ROLES]) }
      : defaultUser;

    try {
      const data: UpdateUserListModel = {
        userList: [parsedUser],
      };
      await UpdateUserList({ host, token, data });
      setMessageSuccess({ description: t('_EDIT_USER_MODAL_SUCCESS_MESSAGE') });
      setIsEditUserModalVisible(false);
      getUserData(userId);
    } catch (error) {
      setMessageError({ description: t('_EDIT_USER_MODAL_ERROR_MESSAGE') });
    } finally {
      setEditingUser(false);
    }
  };

  const onClickClientLink = (clientId: string) => {
    const route = getClientPathById({ clientId });
    history.push(route);
  };

  return {
    user,
    currentUser,
    activeTab,
    userPageLiterals,
    sendNotificationFormLiterals,
    sendNotificationModalLiterals,
    deleteUserModalLiterals,
    form,
    editUserModalLiterals,
    userRoleList,
    clientList,
    currentClient,
    onGoBack,
    onChangeTab,
    handleDeleteUser,
    onShowDeleteUserPop,
    onClickSendNotification,
    onCancelSendNotification,
    onSendNotification,
    onClickEditUser,
    onCloseEditUser,
    onEditUser,
    onClickClientLink,
    showingPop,
    isSendNotificationModalVisible,
    isSendingNotification,
    isEditUserModalVisible,
    deletingUser,
    loading,
    isCurrentUserSuperAdmin,
    editingUser,
  };
};
